import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  Typography,
  Grid,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import DateField from "components/_refactor/fields/date";
import SelectField from "components/_refactor/fields/select";

const useStyles = makeStyles({
  root: {
    marginTop: 16,
  },
  head: {
    background: "transparent",
  },
  headCell: {
    color: "#90A4AE",
    fontWeight: 500,
    textAlign: "center",
    padding: "8px 16px",
  },
  cell: {
    textAlign: "center",
    padding: "5px 0",
    color: "#263238",
    verticalAlign: "middle",
  },
  rowEven: {
    background: "#FAFBFD",
  },
  rowOdd: {
    background: "#fff",
  },
});

const selectStatus = [
  {
    value: "Ativo",
    label: "Ativo",
  },
  {
    value: "Ferias",
    label: "Férias",
  },
  {
    value: "Afastamento",
    label: "Afastamento",
  },
  {
    value: "Desligamento",
    label: "Desligamento",
  },
  {
    value: "Admissões",
    label: "Admissões",
  },
];

export function calculateGoalsDistribution(storeGoal, vendors, month, year) {
  console.log("entrou na função calculateGoalsDistribution");

  if (!vendors || vendors.length === 0) return vendors || [];
  if (!storeGoal || !month || !year)
    return vendors.map((v) => ({ ...v, Meta: 0 }));

  const toCents = (value) => Math.round(Number(value) * 100);
  const fromCents = (cents) => Number((cents / 100).toFixed(2));

  function getDaysInMonth(y, m) {
    return new Date(y, m, 0).getDate();
  }

  function parseBrDate(str) {
    if (!str || typeof str !== "string") return null;
    const parts = str.split(/[-/.]/);
    if (parts.length !== 3) return null;
    const [dd, mm, yyyy] = parts;
    const d = parseInt(dd, 10);
    const m = parseInt(mm, 10);
    const y = parseInt(yyyy, 10);
    if (!d || !m || !y) return null;
    return new Date(y, m - 1, d);
  }

  function formatKey(date) {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${d}/${m}/${y}`;
  }

  const daysInMonth = getDaysInMonth(year, month);
  const totalStoreGoalCents = toCents(storeGoal);
  const baseDailyGoalCents = Math.floor(totalStoreGoalCents / daysInMonth);
  const dailyGoalRemainderCents =
    totalStoreGoalCents - baseDailyGoalCents * daysInMonth;

  const vendorOffDaysMap = new Map();
  vendors.forEach((vendor) => {
    const offDays = new Set();
    let totalOffInPeriod = 0;
    (vendor.DiasOff || []).forEach((d) => {
      let parsed = null;
      if (typeof d === "string") parsed = parseBrDate(d);
      else if (d && typeof d === "object" && typeof d.getMonth === "function")
        parsed = d;

      if (
        parsed &&
        parsed.getMonth() + 1 === month &&
        parsed.getFullYear() === year
      ) {
        const key = formatKey(parsed);
        if (!offDays.has(key)) {
          offDays.add(key);
          totalOffInPeriod += 1;
        }
      }
    });
    vendorOffDaysMap.set(vendor.IdVendedor, {
      offDays,
      totalOff: totalOffInPeriod,
    });
  });

  const totals = new Map();
  vendors.forEach((v) => totals.set(v.IdVendedor, 0));

  for (let day = 1; day <= daysInMonth; day++) {
    const current = new Date(year, month - 1, day);
    const dayKey = formatKey(current);
    const dailyStoreGoalCents =
      baseDailyGoalCents + (day <= dailyGoalRemainderCents ? 1 : 0);

    const activeIds = [];
    const afastadosOffToday = [];

    vendors.forEach((vendor) => {
      const status = vendor.Status;
      const { offDays, totalOff } = vendorOffDaysMap.get(vendor.IdVendedor) || {
        offDays: new Set(),
        totalOff: 0,
      };

      const isListedOffDay = offDays.has(dayKey);

      if (status === "Ferias" || status === "Admissões") {
        if (!isListedOffDay) activeIds.push(vendor.IdVendedor);
        return;
      }

      if (status === "Afastamento" && totalOff > 15) {
        activeIds.push(vendor.IdVendedor);
        if (isListedOffDay) {
          afastadosOffToday.push(vendor.IdVendedor);
        }
        return;
      }

      if (status === "Desligamento") {
        if (totalOff > 15) {
          if (!isListedOffDay) activeIds.push(vendor.IdVendedor);
        } else {
          activeIds.push(vendor.IdVendedor);
        }
        return;
      }

      activeIds.push(vendor.IdVendedor);
    });

    const activeCount = activeIds.length;
    if (activeCount === 0) {
      continue;
    }

    const baseShare = Math.floor(dailyStoreGoalCents / activeCount);
    const remainder = dailyStoreGoalCents - baseShare * activeCount;
    const rotationOffset = day % activeCount;

    activeIds.forEach((id, idx) => {
      const rotatedIdx = (idx + activeCount - rotationOffset) % activeCount;
      const share = baseShare + (rotatedIdx < remainder ? 1 : 0);
      totals.set(id, (totals.get(id) || 0) + share);
    });

    if (afastadosOffToday.length > 0) {
      const activeNonAfastados = activeIds.filter(
        (id) => !afastadosOffToday.includes(id)
      );

      if (activeNonAfastados.length > 0) {
        afastadosOffToday.forEach((afastadoId, afastadoIdx) => {
          const afastadoShare = baseShare;
          const nonAfastadosCount = activeNonAfastados.length;

          const extraShare = Math.floor(afastadoShare / nonAfastadosCount);
          const extraRemainder =
            afastadoShare - extraShare * nonAfastadosCount;
          const extraRotationOffset =
            (day + afastadoIdx) % nonAfastadosCount;

          activeNonAfastados.forEach((id, idx) => {
            const rotatedIdx =
              (idx + nonAfastadosCount - extraRotationOffset) %
              nonAfastadosCount;
            const extra = extraShare + (rotatedIdx < extraRemainder ? 1 : 0);
            totals.set(id, (totals.get(id) || 0) + extra);
          });
        });
      }
    }
  }

  const result = vendors.map((vendor) => ({
    ...vendor,
    Meta: fromCents(totals.get(vendor.IdVendedor) || 0),
  }));
  console.log("result", result);
  return result;
}

export default function TableGoalsBySeller({
  goal,
  data,
  setData,
  onPageChange,
  page,
  totalPages,
  month,
  year,
}) {
  const classes = useStyles();
  console.log("data", data);
  return (
    <div className={classes.root}>
      {data.length > 0 && (
        <>
          <Typography>
            Meta da Loja:{" "}
            {typeof goal === "number"
              ? `R$ ${goal.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : goal}
          </Typography>
          <Table>
            <TableHead>
              <TableRow className={classes.head}>
                <TableCell className={classes.headCell}>Vendedor</TableCell>
                <TableCell className={classes.headCell}>Status</TableCell>
                <TableCell className={classes.headCell}>Dias Off</TableCell>
                <TableCell className={classes.headCell}>Meta</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow
                  key={idx}
                  className={idx % 2 === 0 ? classes.rowEven : classes.rowOdd}
                >
                  <TableCell className={classes.cell}>{row.Nome}</TableCell>
                  <TableCell className={classes.cell}>
                    <SelectField
                      disabled={false}
                      width="10vw"
                      value={row.Status}
                      onChange={(status) => {
                        const updatedData = data.map((item) =>
                          item.IdVendedor === row.IdVendedor
                            ? { ...item, Status: status.target.value }
                            : item
                        );
                        const recalculatedData = calculateGoalsDistribution(
                          goal,
                          updatedData,
                          month.value,
                          year.value
                        );
                        setData(recalculatedData);
                      }}
                      options={selectStatus}
                    />
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <DateField
                      multiple={true}
                      value={row.DiasOff || []}
                      initialViewDate={{ month, year }}
                      onChange={(dates) => {
                        const updatedData = data.map((item) =>
                          item.IdVendedor === row.IdVendedor
                            ? { ...item, DiasOff: dates }
                            : item
                        );
                        const recalculatedData = calculateGoalsDistribution(
                          goal,
                          updatedData,
                          month.value,
                          year.value
                        );
                        setData(recalculatedData);
                      }}
                      pattern="br"
                      label=""
                    />
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {typeof row.Meta === "number"
                      ? `R$ ${row.Meta.toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : row.Meta}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <Grid item xs={12} justifyContent="flex-end">
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={onPageChange}
                color="primary"
                size="small"
                variant="text"
                style={{ margin: "0 auto" }}
              />
            </Grid>
          )}
        </>
      )}
    </div>
  );
}
